import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { Client } from '../../_models/social-domain/client.model';
import { SocialDomainService } from '../../_services/social-domain.service';

/**
 * Cliëntkiezer als zoekveld: de volledige cliëntenlijst wordt één keer opgehaald
 * en daarna lokaal gefilterd, zodat typen direct resultaat geeft zonder per
 * toetsaanslag een call te doen. Een gewone dropdown volstaat niet omdat er
 * duizenden cliënten kunnen zijn.
 */
@Component({
  selector: 'app-client-search',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
  ],
  templateUrl: './client-search.html',
  styleUrl: './client-search.scss',
})
export class ClientSearch implements OnInit {
  /** Cliënt die al eerder in de workflow gekozen is; vult het veld vooraf. */
  @Input() preselected: Client | null = null;
  @Input() label = 'clientSearch.label';

  @Output() clientSelected = new EventEmitter<Client | null>();

  public readonly searchControl = new FormControl<string | Client>('');
  public results: Client[] = [];
  public isLoading = true;
  public loadError = false;
  public noResults = false;

  /** De lijst kan lang zijn; meer opties tonen helpt niemand en vertraagt het paneel. */
  public static readonly MAX_VISIBLE_RESULTS = 50;
  private static readonly NO_MATCH = Number.MAX_SAFE_INTEGER;

  private clients: Client[] = [];

  private readonly socialDomainService = inject(SocialDomainService);

  public ngOnInit(): void {
    if (this.preselected) {
      this.searchControl.setValue(this.preselected);
    }

    this.socialDomainService.getAllClients().subscribe({
      next: (clients) => {
        this.clients = clients ?? [];
        this.isLoading = false;
        this.applyFilter(this.currentTerm());
      },
      error: () => {
        this.isLoading = false;
        this.loadError = true;
      },
    });

    this.searchControl.valueChanges.subscribe((value) => {
      if (typeof value !== 'string') return;

      if (!value.trim()) {
        this.clientSelected.emit(null);
      }
      this.applyFilter(value);
    });
  }

  public onClientSelected(event: MatAutocompleteSelectedEvent): void {
    this.clientSelected.emit(event.option.value as Client);
  }

  public displayClient(client: Client | string | null): string {
    if (!client || typeof client === 'string') return client ?? '';
    return `${client.naam}, ${client.nummer}`;
  }

  private applyFilter(term: string): void {
    const normalized = ClientSearch.normalize(term);

    if (!normalized) {
      this.results = this.clients.slice(0, ClientSearch.MAX_VISIBLE_RESULTS);
      this.noResults = false;
      return;
    }

    const matches = this.clients
      .map((client) => ({ client, score: ClientSearch.matchScore(client, normalized) }))
      .filter((match) => match.score < ClientSearch.NO_MATCH)
      // Beste match bovenaan; bij gelijke score op naam, zodat de volgorde stabiel is.
      .sort((a, b) => a.score - b.score || a.client.naam.localeCompare(b.client.naam));

    this.results = matches.slice(0, ClientSearch.MAX_VISIBLE_RESULTS).map((match) => match.client);
    this.noResults = !this.isLoading && !this.loadError && matches.length === 0;
  }

  /**
   * Lager is beter. Een exact cliëntnummer wint van een nummer dat ermee begint,
   * dat wint weer van een naam die met de term begint, enzovoort — anders verdwijnt
   * cliënt 1 tussen de honderden nummers die ergens een 1 bevatten.
   */
  private static matchScore(client: Client, term: string): number {
    const nummer = String(client.nummer);
    const naam = ClientSearch.normalize(client.naam);

    if (nummer === term) return 0;
    if (nummer.startsWith(term)) return 1;
    if (naam.startsWith(term)) return 2;
    if (naam.includes(term)) return 3;
    if (nummer.includes(term)) return 4;
    return ClientSearch.NO_MATCH;
  }

  private currentTerm(): string {
    const value = this.searchControl.value;
    return typeof value === 'string' ? value : '';
  }

  /** Kleine-letters en zonder accenten, zodat "jansen" ook "Jansén" vindt. */
  private static normalize(value: string): string {
    return (value ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');
  }
}
